import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Buildings, TrendUp, Coins, ArrowSquareOut, ChartPieSlice, Vault } from '@phosphor-icons/react';
import { api } from '../../context/AuthContext';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

const money = (value, { signed = false } = {}) => {
  const n = Number(value || 0);
  const body = `€${Math.abs(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  if (n < 0) return `-${body}`;
  return signed && n > 0 ? `+${body}` : body;
};

const percent = (value) => `${Number(value || 0) > 0 ? '' : ''}${Number(value || 0).toFixed(2)}%`;

const KpiCard = ({ icon: Icon, label, hint, primary, primaryLabel, secondary, secondaryLabel, testId }) => (
  <div className="bg-white rounded-2xl border border-black/[0.06] p-5" data-testid={testId}>
    <div className="flex items-start gap-3">
      <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
        <Icon size={18} weight="duotone" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-medium leading-tight">{label}</p>
        <p className="text-xs text-foreground-muted mt-0.5">{hint}</p>
      </div>
    </div>
    <div className="flex items-end gap-8 mt-5">
      <div>
        <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">{primaryLabel}</p>
        <p className="text-2xl font-semibold tabular-nums mt-1">{primary}</p>
      </div>
      {secondaryLabel && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">{secondaryLabel}</p>
          <p className="text-2xl font-semibold tabular-nums mt-1">{secondary}</p>
        </div>
      )}
    </div>
  </div>
);

const Section = ({ title, hint, children, testId }) => (
  <section className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden" data-testid={testId}>
    <header className="px-5 py-4 border-b border-black/[0.05]">
      <h2 className="text-base font-semibold">{title}</h2>
      {hint && <p className="text-xs text-foreground-muted mt-0.5">{hint}</p>}
    </header>
    {children}
  </section>
);

const Empty = ({ children }) => (
  <p className="px-5 py-8 text-sm text-foreground-muted">{children}</p>
);

export default function AdminFlippexPortfolioPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/v2/flippex/portfolio');
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="p-8 text-sm text-foreground-muted" data-testid="flippex-portfolio-loading">
        Loading Flippex portfolio...
      </div>
    );
  }

  const totals = data?.totals || {};
  const holdings = data?.holdings || [];
  const coInvestments = data?.coInvestments || data?.co_investments || [];
  const income = data?.income || [];

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="flippex-portfolio-page">
      <AdminPageHeader
        eyebrow="Investments"
        title="Flippex portfolio"
        subtitle="What the house owns: units held like any other investor, plus the share of project economics Flippex keeps."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          icon={Vault}
          label="Units held"
          hint="Minted to Flippex, valued like any holding"
          primaryLabel="Invested"
          primary={money(totals.invested)}
          secondaryLabel="Value now"
          secondary={money(totals.current_value)}
          testId="flippex-kpi-holdings"
        />
        <KpiCard
          icon={ChartPieSlice}
          label="Co-investment"
          hint="Share of NAV above what investors subscribed"
          primaryLabel="Projects"
          primary={totals.co_investment_projects || 0}
          secondaryLabel="Stake value"
          secondary={money(totals.co_investment_value)}
          testId="flippex-kpi-coinvestment"
        />
        <KpiCard
          icon={Coins}
          label="Returns retained"
          hint="Distribution share kept by Flippex"
          primaryLabel="Income"
          primary={money(totals.income_retained)}
          secondaryLabel="Unrealised"
          secondary={money(totals.unrealized_change, { signed: true })}
          testId="flippex-kpi-returns"
        />
      </div>

      <Section
        title="Holdings"
        hint="Units minted to Flippex. Open a project to change anything."
        testId="flippex-holdings-section"
      >
        {holdings.length === 0 ? (
          <Empty>No units are held by Flippex yet. Mint units to the Flippex party on an instrument's cap table and they will appear here.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted border-b border-black/[0.05]">
                  <th className="text-left font-medium px-5 py-3">Project</th>
                  <th className="text-left font-medium px-3 py-3">Instrument</th>
                  <th className="text-right font-medium px-3 py-3">Units</th>
                  <th className="text-right font-medium px-3 py-3">Invested</th>
                  <th className="text-right font-medium px-3 py-3">Value now</th>
                  <th className="text-right font-medium px-3 py-3">Change</th>
                  <th className="text-right font-medium px-3 py-3">Income</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {holdings.map((h) => (
                  <tr
                    key={h.position_id}
                    className="border-b border-black/[0.04] last:border-0 hover:bg-black/[0.015] cursor-pointer"
                    onClick={() => h.project_id && navigate(`/admin/v2/projects/${h.project_id}`)}
                    data-testid={`flippex-holding-row-${h.position_id}`}
                  >
                    <td className="px-5 py-3">
                      <span className="flex items-center gap-2">
                        <Buildings size={15} className="text-foreground-muted flex-shrink-0" />
                        <span>
                          <span className="block font-medium">{h.project_title || '—'}</span>
                          <span className="block text-[11px] text-foreground-muted font-mono">{h.project_code}</span>
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="block">{h.instrument_name}</span>
                      <span className="block text-[11px] text-foreground-muted font-mono">{h.instrument_code}</span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">{Number(h.units).toLocaleString('en-GB')}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{money(h.invested)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium">{money(h.current_value)}</td>
                    <td className={`px-3 py-3 text-right tabular-nums ${Number(h.unrealized_change) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                      {money(h.unrealized_change, { signed: true })}
                      <span className="block text-[11px] opacity-70">{percent(h.unrealized_percent)}</span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">{money(h.income_retained)}</td>
                    <td className="px-3 py-3 text-right">
                      <ArrowSquareOut size={14} className="text-foreground-muted inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Co-investment"
        hint="Flippex funds the gap between the asset's value and what investors subscribed, and keeps that share of the economics."
        testId="flippex-coinvestment-section"
      >
        {coInvestments.length === 0 ? (
          <Empty>No project has a starting NAV set, so there is no co-investment split to show yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted border-b border-black/[0.05]">
                  <th className="text-left font-medium px-5 py-3">Project</th>
                  <th className="text-right font-medium px-3 py-3">Starting NAV</th>
                  <th className="text-right font-medium px-3 py-3">Investor raise</th>
                  <th className="text-right font-medium px-3 py-3">Investors</th>
                  <th className="text-right font-medium px-3 py-3">Flippex</th>
                  <th className="text-right font-medium px-3 py-3">Stake value</th>
                  <th className="text-right font-medium px-3 py-3">Retained</th>
                </tr>
              </thead>
              <tbody>
                {coInvestments.map((c) => (
                  <tr
                    key={c.security_id}
                    className="border-b border-black/[0.04] last:border-0 hover:bg-black/[0.015] cursor-pointer"
                    onClick={() => c.project_id && navigate(`/admin/v2/projects/${c.project_id}`)}
                    data-testid={`flippex-coinvestment-row-${c.security_id}`}
                  >
                    <td className="px-5 py-3">
                      <span className="block font-medium">{c.project_title || c.instrument_name}</span>
                      <span className="block text-[11px] text-foreground-muted font-mono">
                        {[c.project_code, c.instrument_code].filter(Boolean).join(' · ')}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">{money(c.starting_nav)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{money(c.investor_raise)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{percent(c.investor_percent)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium text-primary">{percent(c.flippex_percent)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium">{money(c.flippex_stake_value)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{money(c.income_retained)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Returns"
        hint="Distribution shares retained by Flippex. These never enter the investor payment run."
        testId="flippex-returns-section"
      >
        {income.length === 0 ? (
          <Empty>No distribution has been calculated yet, so Flippex has retained nothing.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted border-b border-black/[0.05]">
                  <th className="text-left font-medium px-5 py-3">Distribution</th>
                  <th className="text-left font-medium px-3 py-3">Project</th>
                  <th className="text-left font-medium px-3 py-3">Basis</th>
                  <th className="text-right font-medium px-3 py-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {income.map((r) => (
                  <tr
                    key={r.entitlement_id}
                    className="border-b border-black/[0.04] last:border-0"
                    data-testid={`flippex-income-row-${r.entitlement_id}`}
                  >
                    <td className="px-5 py-3">
                      <span className="block font-mono text-xs">{r.distribution_code || r.distribution_id}</span>
                      <span className="block text-[11px] text-foreground-muted capitalize">
                        {(r.distribution_type || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-3 py-3">{r.project_title || '—'}</td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-medium">
                        <TrendUp size={11} weight="bold" />
                        {Number(r.units_held) > 0
                          ? `${Number(r.units_held).toLocaleString('en-GB')} units held`
                          : 'Co-investment share'}
                      </span>
                      {r.retained && (
                        <span className="block text-[11px] text-foreground-muted mt-1">Retained, not paid out</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium">{money(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
